<template>
  <div class="daily-trend-wrapper">
    <apexchart type="line" :options="chartOptions" :series="series" height="380"></apexchart>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// #772 API sends one row per day as "yyyy-MM-dd"; format it ourselves so the axis gets exactly one label per day
const formatDate = (value, withYear) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (!match) return String(value || '');
  const label = `${match[3]} ${MONTHS[Number(match[2]) - 1]}`;
  return withYear ? `${label} ${match[1]}` : label;
};

export default {
  name: 'DailyTrendChart',
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
  },
  computed: {
    series() {
      return [
        {
          name: 'Users Visit',
          type: 'column',
          data: this.data.map((item) => item.usersVisit),
        },
        {
          name: 'Tax Forms Created',
          type: 'column',
          data: this.data.map((item) => item.taxFormsCreated),
        },
        {
          name: 'Tax Form Complete',
          type: 'column',
          data: this.data.map((item) => item.taxCalculated),
        },
      ];
    },
    chartOptions() {
      const dates = this.data.map((item) => item.date);
      return {
        chart: {
          type: 'line',
          stacked: false,
          toolbar: {
            show: true,
            tools: {
              download: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: false,
              reset: true,
            },
          },
          zoom: {
            enabled: true,
          },
        },
        stroke: {
          width: [0, 0, 0],
          curve: 'smooth',
        },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            borderRadius: 4,
          },
        },
        fill: {
          opacity: [0.85, 0.85, 0.85],
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.4,
            inverseColors: false,
            opacityFrom: 0.85,
            opacityTo: 0.6,
            stops: [0, 100],
          },
        },
        markers: {
          size: [0, 0, 0],
          strokeWidth: 2,
          hover: {
            size: 8,
          },
        },
        // category (not datetime): datetime lets ApexCharts add 12-hour ticks on short ranges, which showed each date twice
        xaxis: {
          type: 'category',
          categories: dates.map((date) => formatDate(date, false)),
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yaxis: [
          {
            seriesName: 'Users Visit',
            title: {
              text: 'Count',
              style: {
                fontSize: '12px',
                fontWeight: 600,
              },
            },
            labels: {
              formatter: function (val) {
                return val ? val.toLocaleString('en-US') : '0';
              },
              style: {
                fontSize: '11px',
              },
            },
          },
          {
            seriesName: 'Users Visit',
            show: false,
          },
          {
            seriesName: 'Users Visit',
            show: false,
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          x: {
            formatter: (val, opts) => formatDate(dates[opts?.dataPointIndex], true) || val,
          },
          y: [
            {
              formatter: function (val) {
                return val ? val.toLocaleString('en-US') + ' users' : '0 users';
              },
            },
            {
              formatter: function (val) {
                return val ? val.toLocaleString('en-US') + ' forms' : '0 forms';
              },
            },
            {
              formatter: function (val) {
                return val ? val.toLocaleString('en-US') + ' forms' : '0 forms';
              },
            },
          ],
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          fontSize: '12px',
          markers: {
            width: 12,
            height: 12,
            radius: 2,
          },
        },
        colors: ['#1976D2', '#43A047', '#FB8C00'],
        grid: {
          borderColor: '#f1f1f1',
          strokeDashArray: 3,
        },
      };
    },
  },
};
</script>

<style scoped>
.daily-trend-wrapper {
  width: 100%;
  min-height: 380px;
}
</style>
